import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ArrowsLeftRightIcon, ArrowUDownLeftIcon, PackageIcon } from '@phosphor-icons/react'

import ProductsCard from '@/metrics/components/ProductsCard/ProductsCard'
import ReasonsCard from '@/metrics/components/ReasonsCard/ReasonsCard'
import ReturnSalesRatioCard from '@/metrics/components/ReturnSalesRatioCard/ReturnSalesRatioCard'
import ReturnsAndExchangesRatioCard from '@/metrics/components/ReturnsAndExchangesRatioCard/ReturnsAndExchangesRatioCard'
import { groupByValues } from '@/metrics/constants'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import { transactionTypes } from '@/returns/constants'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const CategoryMetrics = ({
  categoryId,
  returnPortalId,
  commonFilters,
  returnSalesRatioFilters,
  setReturnSalesRatioFilters,
  returnsAndExchangesRatioFilters,
  setReturnsAndExchangesRatioFilters,
  categoryPosition,
  salesOrdersDataActivated,
  productsOrdering,
  isUnifiedDashboard,
}) => {
  const { t } = useTranslation()
  const navigation = useNavigate()

  const { data: returnsAndExchangesRatioData, loading: loadingReturnsAndExchangesRatio } = useMetrics({
    returnPortalId,
    service: metricsServices.getCategoryReturnsAndExchangesRatio,
    entityId: categoryId,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: returnsAndExchangesRatioFilters,
  })

  const { data: returnSalesRatioData, loading: loadingReturnSalesRatio } = useMetrics({
    returnPortalId,
    service: metricsServices.getCategoryReturnsSalesRatio,
    entityId: categoryId,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: returnSalesRatioFilters,
    doRequest: !!salesOrdersDataActivated,
  })

  const { data: topReturnedProducts, loading: loadingTopReturnedProducts } = useMetrics({
    returnPortalId,
    service: metricsServices.getCategoryTopReturnedProducts,
    entityId: categoryId,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: { ordering: productsOrdering },
  })

  const { data: topReturnReasonsData, loading: loadingTopReturnReasons } = useMetrics({
    returnPortalId,
    service: metricsServices.getCategoryTopReasons,
    entityId: categoryId,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: { transactionType: transactionTypes.refund },
  })

  const { data: topExchangeReasonsData, loading: loadingTopExchangeReasons } = useMetrics({
    returnPortalId,
    service: metricsServices.getCategoryTopReasons,
    entityId: categoryId,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: { transactionType: transactionTypes.exchange },
  })

  const handleClickProduct = productPosition => {
    const currentParams = new URLSearchParams(window.location.search)
    const pathName = isUnifiedDashboard
      ? routes.merchantDashboardProductByCategoryMetricsDetailPage
      : routes.productByCategoryMetricsDetailPage

    navigation({
      pathname: pathName.replace(':categoryPosition', categoryPosition).replace(':productPosition', productPosition),
      search: currentParams.toString(),
    })
  }

  return (
    <>
      <ReturnSalesRatioCard
        filters={{ ...commonFilters, ...returnSalesRatioFilters }}
        data={returnSalesRatioData}
        loading={loadingReturnSalesRatio}
        salesOrdersDataActivated={salesOrdersDataActivated}
        setChartFilters={setReturnSalesRatioFilters}
      />
      <ReturnsAndExchangesRatioCard
        data={returnsAndExchangesRatioData}
        loading={loadingReturnsAndExchangesRatio}
        filters={{ ...commonFilters, ...returnsAndExchangesRatioFilters }}
        setChartFilters={setReturnsAndExchangesRatioFilters}
      />
      <ProductsCard
        title={t('Productos más devueltos')}
        productsFilters={{
          groupBy: groupByValues.products,
          ordering: productsOrdering,
        }}
        products={topReturnedProducts?.results}
        loading={loadingTopReturnedProducts}
        onClickProduct={handleClickProduct}
        Icon={PackageIcon}
        showExport={false}
        showGroupBySelector={false}
        salesOrdersDataActivated={salesOrdersDataActivated}
        isUnifiedDashboard={isUnifiedDashboard}
      />
      <Styles.ColumnsContainer templateColumns={'1fr 1fr'}>
        <ReasonsCard
          title="Motivos de devolución"
          icon={<ArrowUDownLeftIcon size={24} />}
          reasons={topReturnReasonsData?.results}
          loading={loadingTopReturnReasons}
          reasonType={transactionTypes.refund}
          shouldShowExport={false}
        />
        <ReasonsCard
          title="Motivos de cambio"
          icon={<ArrowsLeftRightIcon size={24} />}
          reasons={topExchangeReasonsData?.results}
          loading={loadingTopExchangeReasons}
          reasonType={transactionTypes.exchange}
          shouldShowExport={false}
        />
      </Styles.ColumnsContainer>
    </>
  )
}

export default CategoryMetrics
